import deepEqual from "fast-deep-equal";
import { useEffect, useRef } from "react";

import { useStableRef } from "./useStableRef";

/**
 * Runs an onChange callback whenever the provided value changes
 * - the callback is always called on initial mount
 * - in order to skip the initial callback, check if previousValue == undefined
 * - it is ok for the callback to trigger side-effects, as it is called from useEffect()
 * - safe for concurrent-rendering since onChange side-effects run in an effect and the previous value check prevents re-running onChange when the effect re-runs
 */
export function useOnValueChange<T>(
  value: T,
  onChange: (previousValue: T | undefined) => void,
) {
  const previousValueRef = useRef<T | undefined>(undefined);
  const stableOnChange = useStableRef(onChange);

  useEffect(() => {
    const previousValue = previousValueRef.current;
    if (!Object.is(value, previousValue)) {
      previousValueRef.current = value;
      stableOnChange.current(previousValue);
    }
  }, [stableOnChange, value]);
}

/**
 * Runs an onChange callback whenever the provided value changes (using deep-equal comparison)
 * - the callback is always called on initial mount
 * - in order to skip the initial callback, check if previousValue == undefined
 * - it is ok for the callback to trigger side-effects, as it is called from useEffect()
 * - safe for concurrent-rendering since onChange side-effects run in an effect and the previous value check prevents re-running onChange when the effect re-runs
 */
export function useOnValueDeeplyChange<T>(
  value: T,
  onChange: (previousValue: T | undefined) => void,
) {
  const previousValueRef = useRef<T | undefined>(undefined);
  const stableOnChange = useStableRef(onChange);

  useEffect(() => {
    const previousValue = previousValueRef.current;
    if (!deepEqual(value, previousValue)) {
      previousValueRef.current = value;
      stableOnChange.current(previousValue);
    }
  }, [stableOnChange, value]);
}
